import {BiqliError, asBiqliError} from './errors';
import type {
  BiqliErrorBody,
  ClickResponse,
  EventResponse,
  SaleResponse,
  StripeSessionResponse,
  TrackingEventKind,
} from './types';
import {createId, endpointUrl} from './utils';

type TrackingResponse = ClickResponse | EventResponse | SaleResponse;

const ENDPOINTS: Record<TrackingEventKind, string> = {
  click: 'api/v1/track/click',
  lead: 'api/v1/track/lead/client',
  sale: 'api/v1/track/sale/client',
};

export class TrackingTransport {
  private readonly fetcher: typeof globalThis.fetch;

  constructor(
    private readonly apiHost: string,
    private readonly publishableKey: string,
    private readonly timeoutMs: number,
    private readonly clientName: string,
    private readonly clientVersion: string,
    fetcher?: typeof globalThis.fetch,
  ) {
    const resolved = fetcher ?? globalThis.fetch;
    if (typeof resolved !== 'function') {
      throw new BiqliError({
        message: 'This browser does not provide the Fetch API.',
        code: 'fetch_unavailable',
      });
    }
    this.fetcher = resolved.bind(globalThis);
  }

  async send<T extends TrackingResponse>(
    kind: TrackingEventKind,
    payload: Record<string, unknown>,
    idempotencyKey: string,
  ): Promise<T> {
    return this.request<T>(ENDPOINTS[kind], payload, {
      Authorization: `Bearer ${this.publishableKey}`,
      'Idempotency-Key': idempotencyKey,
      'X-Biq-Client': this.clientName,
      'X-Biq-Client-Version': this.clientVersion,
      'X-Biq-Request-Id': createId('req'),
    });
  }

  async storeStripeSession(payload: Record<string, unknown>): Promise<StripeSessionResponse> {
    return this.request<StripeSessionResponse>(
      'api/v1/integrations/stripe/checkout-sessions',
      payload,
      {
        Authorization: `Bearer ${this.publishableKey}`,
        'Idempotency-Key': createId('stripe_checkout'),
        'X-Biq-Client': this.clientName,
        'X-Biq-Client-Version': this.clientVersion,
        'X-Biq-Request-Id': createId('req'),
      },
    );
  }

  private async request<T>(
    path: string,
    payload: Record<string, unknown>,
    headers: Record<string, string>,
  ): Promise<T> {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? globalThis.setTimeout(() => controller.abort(), this.timeoutMs) : null;
    try {
      const response = await this.fetcher(endpointUrl(this.apiHost, path), {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        redirect: 'error',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify(payload),
        signal: controller?.signal,
      });
      const requestId = response.headers.get('X-Biq-Request-Id');
      const body = await this.readBody(response);
      if (!response.ok) {
        throw BiqliError.fromResponse(response.status, body as BiqliErrorBody, requestId);
      }
      return body as T;
    } catch (error) {
      if (error instanceof BiqliError) throw error;
      if (controller?.signal.aborted) {
        throw new BiqliError({
          message: 'The tracking request timed out.',
          code: 'request_timeout',
          retryable: true,
          cause: error,
        });
      }
      throw asBiqliError(error);
    } finally {
      if (timer !== null) globalThis.clearTimeout(timer);
    }
  }

  private async readBody(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text) as unknown;
    } catch (error) {
      throw new BiqliError({
        message: 'The tracking service returned an invalid response.',
        code: 'invalid_response',
        status: response.status,
        requestId: response.headers.get('X-Biq-Request-Id'),
        retryable: response.status >= 500,
        cause: error,
      });
    }
  }
}
